import { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    Button,
    TextInput,
    NumberInput,
    Box,
    Title,
    rem,
    Select,
    MultiSelect,
    useMantineColorScheme,
    ThemeIcon,
    Image,
    Group,
    Tooltip
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { ArrowRight, Check, ChevronUp, AlertCircle } from 'lucide-react';
import type { Form } from '@bi-studio/shared/lib';
import type { UseFormReturnType } from '@mantine/form';
import { motion, AnimatePresence } from 'framer-motion';
import logo from "@bi-studio/shared/assets/logo.png";

interface StepsFormProps {
    form: Form;
    mantineForm: UseFormReturnType<Record<string, any>>;
    onSubmit: (values: any) => Promise<void>;
    submitting: boolean;
}

export function StepsForm({ form, mantineForm, onSubmit, submitting }: StepsFormProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { colorScheme } = useMantineColorScheme();
    const sortedFields = [...form.fields].sort((a, b) => a.order - b.order);
    const totalSteps = sortedFields.length + 1; // +1 for the welcome screen
    const progress = (currentStep / (totalSteps - 1)) * 100;

    const currentField = currentStep > 0 ? sortedFields[currentStep - 1] : null;

    // Scroll to top on step change for mobile
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentStep]);



    const handleNext = async () => {
        if (currentStep === 0) {
            setCurrentStep(1);
            return;
        }

        if (!currentField) return;

        // Validate current field
        const hasError = mantineForm.validateField(currentField.name).hasError;
        if (!hasError) {
            if (currentStep < totalSteps - 1) {
                setCurrentStep(prev => prev + 1);
            } else {
                // It's the last step, submit
                await onSubmit(mantineForm.values);
                setIsSubmitted(true);
            }
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };



    // Auto-focus logic can be handled by the 'autoFocus' prop mostly, 
    // but for complex transitions, explicit ref focus might be needed. 
    // With AnimatePresence, the new component mounts, so standard autoFocus usually works.

    if (isSubmitted) {
        return (
            <div className="flex flex-col justify-center items-center min-h-[300px] w-full max-w-2xl mx-auto px-4 sm:px-6 relative text-center">
                <Box
                    style={{
                        backgroundColor: colorScheme === 'dark' ? 'rgba(29, 30, 32, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: 'var(--mantine-radius-xl)',
                        border: '1px solid var(--modal-border)',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                        overflow: 'hidden',
                        padding: rem(24),
                        width: '100%',
                        minHeight: rem(300),
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5, ease: "backOut" }}
                    >
                        <ThemeIcon size={80} radius="xl" color="green" variant="light" mb="xl">
                            <Check size={40} strokeWidth={3} />
                        </ThemeIcon>
                    </motion.div>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <Title order={1} mb="sm">
                            Thank You!
                        </Title>
                        <Text size="lg" c="dimmed" maw={500} mx="auto">
                            Your submission has been received successfully.
                        </Text>
                    </motion.div>
                </Box>

                {/* App Footer/Branding */}
                <Box p="md" mt="xl" style={{
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <Group justify="center" gap="xs">
                        <Text size="xs" c="dimmed" fw={600} style={{ letterSpacing: rem(1) }}>
                            Powered by
                        </Text>
                        <Image
                            src={typeof logo === 'string' ? logo : (logo as any).src}
                            alt="BI Studio Logo"
                            h={24}
                            w="auto"
                            style={{
                                mixBlendMode: colorScheme === 'dark' ? 'screen' : 'multiply',
                                filter: colorScheme === 'dark' ? 'invert(1) hue-rotate(180deg) brightness(1.2)' : 'none',
                                opacity: 0.7
                            }}
                        />
                    </Group>
                </Box>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-[300px] w-full max-w-2xl mx-auto px-4 sm:px-6 relative">


            {/* Content Area */}
            <div className="flex-1 flex flex-col justify-center py-2 sm:py-8">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="w-full"
                    >
                        <Box
                            className="bg-white/90 dark:bg-[#1d1e20]/80 backdrop-blur-[20px] rounded-[var(--mantine-radius-xl)] border border-[var(--modal-border)] shadow-[0_20px_40px_rgba(0,0,0,0.1)] overflow-hidden p-5 sm:p-10 min-h-[320px] sm:min-h-[500px] flex flex-col justify-center relative"
                        >
                            {/* Progress Bar - In-container styled */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: rem(4),
                                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                zIndex: 5
                            }}>
                                <motion.div
                                    style={{
                                        height: '100%',
                                        backgroundColor: 'var(--mantine-color-blue-6)',
                                        boxShadow: '0 0 10px rgba(77, 171, 247, 0.5)'
                                    }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                />
                            </div>

                        {currentStep === 0 ? (
                            // Welcome Screen
                            <div className="text-center space-y-6 max-w-xl mx-auto">
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.1 }}
                                >
                                    <Title order={1} size={rem(28)} fw={900} style={{ lineHeight: 1.1, letterSpacing: '-0.02em' }} className="text-gray-900 dark:text-white sm:text-[36px]">
                                        {form.name}
                                    </Title>
                                </motion.div>

                                {form.description && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                    >
                                        <Text size="md" c="dimmed" style={{ lineHeight: 1.6 }} className="sm:text-lg">
                                            {form.description}
                                        </Text>
                                    </motion.div>
                                )}

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="pt-6"
                                >
                                    <Button
                                        size="md"
                                        radius="md"
                                        onClick={handleNext}
                                        color="black"
                                        rightSection={<ArrowRight size={18} />}
                                    >
                                        Get Started
                                    </Button>
                                </motion.div>
                            </div>
                        ) : (
                            // Form Fields
                            <div className="max-w-xl mx-auto w-full">
                                {/* Question Header */}
                                <Stack gap="xs" mb="md" className="sm:mb-lg">
                                    <Title order={2} size={rem(20)} fw={600} style={{ lineHeight: 1.3 }} className="sm:text-[24px]">
                                        {currentField!.label}
                                        {currentField!.required && <Text span c="red" ml={4}>*</Text>}
                                    </Title>
                                </Stack>

                                {/* Input Area */}
                                <Box>
                                    {currentField!.type === 'number' && (
                                        <NumberInput
                                            size="md"
                                            placeholder="Type your answer..."
                                            classNames={{
                                                input: `text-lg sm:text-2xl font-light py-3 sm:py-4 px-4 sm:px-5 rounded-lg transition-all duration-200 bg-gray-50 dark:bg-zinc-800/50 border ${mantineForm.errors[currentField!.name]
                                                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 placeholder:text-red-300'
                                                    : 'border-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-gray-900 dark:text-white placeholder:text-gray-400'
                                                    }`,
                                                wrapper: 'shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg'
                                            }}
                                            rightSection={mantineForm.errors[currentField!.name] && <AlertCircle size={20} className="text-red-500 mr-3" />}
                                            {...mantineForm.getInputProps(currentField!.name)}
                                            error={false}
                                            autoFocus
                                            hideControls
                                        />
                                    )}

                                    {currentField!.type === 'date' && (
                                        <DateInput
                                            size="md"
                                            placeholder="Select a date..."
                                            valueFormat="DD MMMM YYYY"
                                            classNames={{
                                                input: `text-lg sm:text-2xl font-light py-3 sm:py-4 px-4 sm:px-5 rounded-lg transition-all duration-200 bg-gray-50 dark:bg-zinc-800/50 border cursor-pointer ${mantineForm.errors[currentField!.name]
                                                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 placeholder:text-red-300'
                                                    : 'border-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-gray-900 dark:text-white placeholder:text-gray-400'
                                                    }`,
                                                wrapper: 'shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg'
                                            }}
                                            rightSection={mantineForm.errors[currentField!.name] && <AlertCircle size={20} className="text-red-500 mr-3" />}
                                            {...mantineForm.getInputProps(currentField!.name)}
                                            error={false}
                                            autoFocus
                                            clearable
                                        />
                                    )}

                                    {currentField!.type === 'checkbox' && (
                                        <div className="space-y-3">
                                            <Box
                                                component={motion.div}
                                                whileHover={{ scale: 1.01, y: -1 }}
                                                whileTap={{ scale: 0.99 }}
                                                onClick={() => mantineForm.setFieldValue(currentField!.name, !mantineForm.values[currentField!.name])}
                                                style={{
                                                    cursor: 'pointer',
                                                    padding: rem(20),
                                                    borderRadius: 'var(--mantine-radius-lg)',
                                                    border: '1px solid var(--modal-border)',
                                                    transition: 'all 0.3s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rem(16),
                                                    backgroundColor: mantineForm.values[currentField!.name]
                                                        ? 'var(--mantine-color-blue-6)'
                                                        : (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.7)'),
                                                    boxShadow: 'var(--mantine-shadow-sm)',
                                                    backdropFilter: 'blur(10px)',
                                                    borderColor: mantineForm.values[currentField!.name] ? 'var(--mantine-color-blue-6)' : 'var(--modal-border)'
                                                }}
                                            >
                                                <Box style={{
                                                    width: rem(40),
                                                    height: rem(40),
                                                    borderRadius: 'var(--mantine-radius-md)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.3s ease',
                                                    backgroundColor: mantineForm.values[currentField!.name]
                                                        ? 'rgba(255, 255, 255, 0.2)'
                                                        : (colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'),
                                                    color: mantineForm.values[currentField!.name] ? 'white' : 'var(--mantine-color-dimmed)'
                                                }}>
                                                    {mantineForm.values[currentField!.name] && <Check size={20} strokeWidth={3} />}
                                                </Box>
                                                <div className="flex-1">
                                                    <Text size="md" fw={600} c={mantineForm.values[currentField!.name] ? "white" : undefined} className="sm:text-lg">
                                                        Yes, I agree
                                                    </Text>
                                                    <Text size="xs" c={mantineForm.values[currentField!.name] ? "blue.0" : "dimmed"} className="sm:text-sm">
                                                        Click to confirm
                                                    </Text>
                                                </div>
                                            </Box>
                                        </div>
                                    )}

                                    {currentField!.type === 'select' && (
                                        <Select
                                            size="md"
                                            placeholder=""
                                            data={currentField!.options || []}
                                            classNames={{
                                                input: `text-lg sm:text-2xl font-light py-3 sm:py-4 px-4 sm:px-5 rounded-lg transition-all duration-200 bg-gray-50 dark:bg-zinc-800/50 border ${mantineForm.errors[currentField!.name]
                                                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 placeholder:text-red-300'
                                                    : 'border-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-gray-900 dark:text-white placeholder:text-gray-400'
                                                    }`,
                                                wrapper: 'shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg',
                                                dropdown: 'rounded-lg shadow-lg border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800',
                                                option: 'py-2 px-3 hover:bg-gray-50 dark:hover:bg-zinc-700/50 rounded-md transition-colors'
                                            }}
                                            rightSection={mantineForm.errors[currentField!.name] && <AlertCircle size={20} className="text-red-500 mr-3" />}
                                            {...mantineForm.getInputProps(currentField!.name)}
                                            error={false}
                                            autoFocus
                                            comboboxProps={{ transitionProps: { transition: 'pop', duration: 200 } }}
                                        />
                                    )}

                                    {currentField!.type === 'multiselect' && (
                                        <MultiSelect
                                            size="md"
                                            placeholder={mantineForm.values[currentField!.name]?.length > 0 ? "" : "Select options..."}
                                            data={currentField!.options || []}
                                            classNames={{
                                                input: `text-lg sm:text-2xl font-light py-2 sm:py-3 pl-3 sm:pl-4 pr-10 sm:pr-12 rounded-lg transition-all duration-200 bg-gray-50 dark:bg-zinc-800/50 border flex items-center flex-wrap gap-1 sm:gap-2 ${mantineForm.errors[currentField!.name]
                                                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 placeholder:text-red-300'
                                                    : 'border-transparent focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/10 text-gray-900 dark:text-white placeholder:text-gray-400'
                                                    }`,
                                                wrapper: 'shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg',
                                                dropdown: 'rounded-lg shadow-lg border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800',
                                                option: 'py-2 px-3 hover:bg-gray-50 dark:hover:bg-zinc-700/50 rounded-md transition-colors',
                                                pill: 'bg-white dark:bg-zinc-700 border border-gray-200 dark:border-zinc-600 text-sm sm:text-base h-auto py-0.5 sm:py-1 px-2 sm:px-3 m-0',
                                                inputField: 'text-lg sm:text-2xl font-light',
                                            }}
                                            rightSection={mantineForm.errors[currentField!.name] && <AlertCircle size={20} className="text-red-500 mr-3" />}
                                            {...mantineForm.getInputProps(currentField!.name)}
                                            error={false}
                                            autoFocus
                                            comboboxProps={{ transitionProps: { transition: 'pop', duration: 200 } }}
                                        />
                                    )}

                                    {(currentField!.type === 'text' || !['number', 'date', 'checkbox', 'select', 'multiselect'].includes(currentField!.type)) && (
                                        <TextInput
                                            size="md"
                                            placeholder="Type your answer..."
                                            classNames={{
                                                input: `text-lg sm:text-2xl font-light py-3 sm:py-4 px-4 sm:px-5 rounded-lg transition-all duration-200 bg-gray-50 dark:bg-zinc-800/50 border ${mantineForm.errors[currentField!.name]
                                                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-600 placeholder:text-red-300'
                                                    : 'border-transparent focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 text-gray-900 dark:text-white placeholder:text-gray-400'
                                                    }`,
                                                wrapper: 'shadow-sm hover:shadow-md transition-shadow duration-200 rounded-lg'
                                            }}
                                            rightSection={mantineForm.errors[currentField!.name] && <AlertCircle size={20} className="text-red-500 mr-3" />}
                                            {...mantineForm.getInputProps(currentField!.name)}
                                            error={false}
                                            autoFocus
                                        />
                                    )}

                                    {/* Error Message Animation */}
                                    <AnimatePresence>
                                        {mantineForm.errors[currentField!.name] && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0, y: -10 }}
                                                animate={{ opacity: 1, height: 'auto', y: 0 }}
                                                exit={{ opacity: 0, height: 0, y: -10 }}
                                                className="mt-2 flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 py-1.5 px-3 rounded-md w-fit"
                                            >
                                                <AlertCircle size={14} />
                                                <Text size="sm" fw={500}>{mantineForm.errors[currentField!.name]}</Text>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Box>

                                {/* Navigation Bar */}
                                <div className="mt-6 sm:mt-8 flex items-center justify-between gap-2">
                                    <Tooltip label="Back to previous question" position="top" withArrow transitionProps={{ transition: 'fade', duration: 200 }}>
                                        <Button
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            onClick={handlePrevious}
                                            disabled={currentStep <= 1}
                                            style={{
                                                opacity: currentStep <= 1 ? 0 : 1,
                                                pointerEvents: currentStep <= 1 ? 'none' : 'auto'
                                            }}
                                            leftSection={<ChevronUp size={18} />}
                                            className="px-2 sm:px-3"
                                        >
                                            <span className="hidden xs:inline">Previous</span>
                                        </Button>
                                    </Tooltip>

                                    <div className="flex items-center gap-2 sm:gap-4">
                                        <Button
                                            size="md"
                                            onClick={handleNext}
                                            loading={submitting && currentStep === totalSteps - 1}
                                            color={currentStep === totalSteps - 1 ? "green" : "black"}
                                            rightSection={currentStep === totalSteps - 1 ? <Check size={18} /> : <ArrowRight size={18} />}
                                            className="px-4 sm:px-6"
                                        >
                                            {currentStep === totalSteps - 1 ? 'Submit' : (
                                                <>
                                                    <span className="hidden xs:inline">Next Step</span>
                                                    <span className="xs:hidden">Next</span>
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                        </Box>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* App Footer/Branding */}
            <Box p="md" style={{
                textAlign: 'center',
                width: '100%',
                marginTop: 'auto'
            }}>
                <Group justify="center" gap="xs">
                    <Text size="xs" c="dimmed" fw={600} style={{ letterSpacing: rem(1) }}>
                        Powered by
                    </Text>
                    <Image
                        src={typeof logo === 'string' ? logo : (logo as any).src}
                        alt="BI Studio Logo"
                        h={24}
                        w="auto"
                        style={{
                            mixBlendMode: colorScheme === 'dark' ? 'screen' : 'multiply',
                            filter: colorScheme === 'dark' ? 'invert(1) hue-rotate(180deg) brightness(1.2)' : 'none',
                            opacity: 0.7
                        }}
                    />
                </Group>
            </Box>
        </div>
    );
}
